import { Dialog, DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import {
  ChangeDetectorRef,
  DestroyRef,
  Directive,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { AbstractControl, FormBuilder, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import {
  ActiveProviderData,
  CreateProviderAppointmentDto,
  CreateProviderAppointmentResponse,
  PaymentCollectionStrategy,
  ProviderAppointmentMode,
  ProviderAppointmentType,
  ProviderPatient,
  ProviderTimezoneUpdateResponse,
  SlotsResponse,
  Timezone,
} from '@models';
import {
  ActiveProviderService,
  AppointmentActor,
  AppointmentConsentCompletionService,
  AppointmentsService,
  AppointmentTypeDisplayService,
  DateParserService,
  LANGUAGE_INFO,
  ProfileTimeZoneService,
  ProviderAppointmentTypesService,
  ProviderAvailabilityService,
  ProviderPatientsService,
  ProviderService,
  Utils,
} from '@services';
import { APPOINTMENT_NOTE_MAX_LENGTH, triggerValidation } from '@ui-lib';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  defer,
  distinctUntilChanged,
  EMPTY,
  finalize,
  map,
  Observable,
  of,
  startWith,
  Subject,
  switchMap,
  take,
  tap,
} from 'rxjs';

interface ProviderLanguage {
  languageCode: string;
  languageFlagUrl: string;
  languageName: string;
}

type SlotDay = SlotsResponse['days'][string];
type SelectedAppointmentType = ProviderAppointmentType & {
  checkoutRequired: boolean;
};

@Directive()
export abstract class BookAppointmentDialogBaseComponent implements OnInit {
  protected abstract readonly actor: AppointmentActor;

  protected readonly appointmentsService = inject(AppointmentsService);
  protected readonly providerAvailabilityService = inject(
    ProviderAvailabilityService
  );
  protected readonly providerService = inject(ProviderService);
  protected readonly providerPatientsService = inject(ProviderPatientsService);
  protected readonly activeProviderService = inject(ActiveProviderService);
  protected readonly profileTimeZone = inject(ProfileTimeZoneService);
  protected readonly appointmentConsent = inject(
    AppointmentConsentCompletionService
  );
  protected readonly dialogRef = inject<
    DialogRef<CreateProviderAppointmentResponse | null>
  >(DialogRef<CreateProviderAppointmentResponse | null>);
  protected readonly formBuilder = inject(FormBuilder);
  protected readonly destroyRef = inject(DestroyRef);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  protected readonly providerAppointmentTypesService = inject(
    ProviderAppointmentTypesService
  );
  protected readonly appointmentTypeDisplayService = inject(
    AppointmentTypeDisplayService
  );
  protected readonly dateParser = inject(DateParserService);
  protected readonly dialog = inject(Dialog);
  protected readonly router = inject(Router);
  protected readonly dialogData = inject<{
    patientId?: string;
    activeProvider?: ActiveProviderData | null;
    requestedStartUtc?: string;
    timeZoneId?: string;
  }>(DIALOG_DATA);

  protected readonly requestedStartUtc =
    this.dialogData?.requestedStartUtc ?? null;

  protected readonly appointmentTypesManagementUrl: string | null = null;

  activeProvider = this.dialogData?.activeProvider
    ? signal<ActiveProviderData | null>(this.dialogData.activeProvider)
    : toSignal<ActiveProviderData | null>(
        this.activeProviderService.getActiveProviderData(),
        { initialValue: null }
      );

  private providerLanguagesMap = new Map<string, ProviderLanguage>();
  get providerLanguages(): ProviderLanguage[] {
    return Array.from(this.providerLanguagesMap.values());
  }

  private appointmentTypesMap = new Map<string, ProviderAppointmentType>();
  get appointmentTypes(): ProviderAppointmentType[] {
    return Array.from(this.appointmentTypesMap.values());
  }

  protected patientsList: ProviderPatient[] = [];
  filteredPatients: ProviderPatient[] = [];

  protected timezones = [] as Timezone[];
  filteredTimezones: Timezone[] = [];

  protected providerTimezone: string | undefined = undefined;

  private _prevAppointmentType?: string;
  _prevTimezone?: string;
  _prevDateRange?: { start: string; end: string } | null;

  private _isInitialRangeEmission = true;

  selectedDate$ = new Subject<Date>();
  selectedDateRange$ = new Subject<{ start: string; end: string }>();
  get selectedDate(): Observable<string> {
    return this.selectedDate$
      .asObservable()
      .pipe(map(date => date.toISOString().split('T')[0]));
  }

  appointmentForm = this.formBuilder.nonNullable.group({
    language: ['', [Validators.required]],
    patientId: [this.dialogData?.patientId ?? '', [Validators.required]],
    appointmentType: ['', [Validators.required]],
    date: ['', [Validators.required]],
    timeslot: ['', [Validators.required]],
    timezoneId: ['', Validators.required],
    note: ['', [Validators.maxLength(APPOINTMENT_NOTE_MAX_LENGTH)]],
  });

  get isProviderDialog(): boolean {
    return this.actor === 'provider';
  }

  get isPatientDialog(): boolean {
    return this.actor === 'patient';
  }

  get hasPrefilledPatientId(): boolean {
    return !!this.dialogData?.patientId;
  }

  get timezoneIdControl(): AbstractControl | null {
    return this.appointmentForm.get('timezoneId');
  }

  get patientIdControl(): AbstractControl | null {
    return this.appointmentForm.get('patientId');
  }

  minDate: string = (() => {
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-01`;
  })();

  maxDate = signal<string>(
    (() => {
      const today = new Date();
      const max = new Date(
        today.getFullYear(),
        today.getMonth() + 2,
        today.getDate()
      );
      return `${max.getFullYear()}-${String(max.getMonth() + 1).padStart(2, '0')}-${String(max.getDate()).padStart(2, '0')}`;
    })()
  );

  currentTimezone$ = new BehaviorSubject<string>(
    this.timezoneIdControl?.value || this.profileTimeZone.timeZone()
  );

  loadingSlots = signal(false);

  get languageControl(): AbstractControl {
    return this.appointmentForm.get('language') as AbstractControl;
  }

  get appointmentTypeControl(): AbstractControl {
    return this.appointmentForm.get('appointmentType') as AbstractControl;
  }

  get selectedAppointmentType(): SelectedAppointmentType | null {
    const appointmentTypeId = this.appointmentTypeControl?.value;
    if (!appointmentTypeId) return null;
    if (!this.appointmentTypesMap.has(appointmentTypeId)) return null;

    const appointmentType = this.appointmentTypesMap.get(appointmentTypeId)!;
    return {
      ...appointmentType,
      checkoutRequired:
        appointmentType.paymentRequired ||
        ((appointmentType.cost ?? 0) > 0 &&
          appointmentType.paymentCollectionStrategy ===
            PaymentCollectionStrategy.SetupWithoutCharging),
    };
  }

  get dateControl(): AbstractControl {
    return this.appointmentForm.get('date') as AbstractControl;
  }

  get timeslotControl(): AbstractControl {
    return this.appointmentForm.get('timeslot') as AbstractControl;
  }

  requestedStartMessage = signal<string | null>(null);

  private providerChoseTimeslot = false;

  noActiveTypes = signal(false);

  private timeslotDaysCache: Record<string, SlotDay> = {};
  private slotDurationMinutesCache = 0;
  private viewerTimeZoneCache = '';

  enabledDates: string[] | null = null;

  timeslots$: Observable<SlotsResponse> = combineLatest([
    this.selectedDateRange$,
    defer(() => {
      const changes = this.appointmentTypeControl?.valueChanges ?? of(null);

      return this.requestedStartUtc
        ? changes.pipe(
            startWith(this.appointmentTypeControl?.value ?? ''),
            distinctUntilChanged()
          )
        : changes.pipe(distinctUntilChanged());
    }),
    this.currentTimezone$ ?? of(null),
  ]).pipe(
    switchMap(([dateRange, appointmentTypeId, timezoneId]) => {
      if (!this._prevAppointmentType) {
        this._prevAppointmentType = undefined;
      }
      if (!this._prevTimezone) {
        this._prevTimezone = undefined;
      }
      if (!this._prevDateRange) {
        this._prevDateRange = undefined;
      }

      if (
        this._prevAppointmentType !== appointmentTypeId ||
        this._prevTimezone !== timezoneId
      ) {
        this.timeslotDaysCache = {};
        this.slotDurationMinutesCache = 0;
        this.viewerTimeZoneCache = '';
      }

      this._prevAppointmentType = appointmentTypeId;
      this._prevTimezone = timezoneId;
      this._prevDateRange = dateRange;

      if (!dateRange || !appointmentTypeId) {
        this.loadingSlots.set(false);
        return of({
          slotDurationMinutes: 0,
          viewerTimeZone: '',
          days: { ...this.timeslotDaysCache },
        } as SlotsResponse);
      }

      this.errorMessages.set([]);
      this.loadingSlots.set(true);

      return this.appointmentsService
        .getSlots(
          this.activeProvider()!.providerId,
          appointmentTypeId.split('/')[0],
          dateRange.start,
          dateRange.end,
          timezoneId
        )
        .pipe(
          tap(result => {
            this.timeslotDaysCache = {
              ...this.timeslotDaysCache,
              ...result.days,
            };
            this.slotDurationMinutesCache = result.slotDurationMinutes;
            this.viewerTimeZoneCache = result.viewerTimeZone;
            this.maxDate.set(result.maxBookableDateIso);
          }),
          catchError(error => {
            if (
              error?.status === 404 &&
              error?.error?.code === 'APPOINTMENT_TYPE_UNAVAILABLE'
            ) {
              this.errorMessages.set([
                'Provider or appointment type not found, or appointment type not available for booking.',
              ]);
            }
            return of({
              slotDurationMinutes: 0,
              viewerTimeZone: '',
              days: { ...this.timeslotDaysCache },
              maxBookableDateIso: this.maxDate(),
            } as SlotsResponse);
          }),
          map(() => ({
            slotDurationMinutes: this.slotDurationMinutesCache,
            viewerTimeZone: this.viewerTimeZoneCache,
            days: { ...this.timeslotDaysCache },
            maxBookableDateIso: this.maxDate(),
          })),
          map(result => ({
            ...result,
            days: Object.keys(result.days).reduce(
              (filteredDays, dateKey) => {
                const now = new Date();
                filteredDays[dateKey] = result.days[dateKey].filter(slot => {
                  const slotTime = new Date(slot.startUtc);
                  return slotTime > now;
                });
                return filteredDays;
              },
              {} as Record<string, SlotDay>
            ),
          })),
          tap(result => {
            this.enabledDates = Object.keys(result.days).filter(
              dateKey => result.days[dateKey] && result.days[dateKey].length > 0
            );
            if (!this.requestedStartUtc && this.enabledDates?.length) {
              const current = this.dateControl.value;

              const toTime = (iso?: string) =>
                iso ? new Date(iso).getTime() : NaN;

              if (!this.enabledDates.includes(current)) {
                const nowTime = toTime(current) || Date.now();

                let chosen = this.enabledDates.find(d => toTime(d) >= nowTime);

                if (!chosen) {
                  chosen = this.enabledDates[this.enabledDates.length - 1];
                }

                if (chosen) {
                  this.dateControl.setValue(chosen, { emitEvent: false });
                }
              }
            }
          }),
          tap(result => this.applyRequestedStart(result, timezoneId)),
          finalize(() => {
            this.loadingSlots.set(false);
          })
        );
    })
  );

  initialLoading = signal(false);
  errorMessages = signal<string[]>([]);
  submitting = signal(false);

  ngOnInit(): void {
    this.timezoneIdControl?.valueChanges
      .pipe(
        startWith(''),
        map(value => this._filterTimezones(value || ''))
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(filteredTimezones => {
        this.filteredTimezones = filteredTimezones;
      });

    this.patientIdControl?.valueChanges
      .pipe(
        startWith(''),
        map(value => this._filterPatients(value || ''))
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(filteredPatients => {
        this.filteredPatients = filteredPatients;
      });

    this.appointmentTypeControl.updateValueAndValidity();

    this.dateControl?.valueChanges
      .pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.appointmentForm
          .get('timeslot')
          ?.setValue('', { emitEvent: false });
        this.refreshRequestedStartMessage(this.timeslotDaysCache);
        this.changeDetectorRef.markForCheck();
      });

    this.timeslotControl?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(value => {
        if (value) {
          this.providerChoseTimeslot = true;
          this.requestedStartMessage.set(null);
        }
      });

    this.fetchInitialData();
  }

  protected abstract fetchInitialData(): void;

  protected getCommonBookingRequests() {
    return {
      providerLanguages: this.providerService.getProviderLanguages(
        this.activeProvider()!.providerId
      ),
      appointmentTypes:
        this.providerAppointmentTypesService.getAppointmentTypes(true, {
          providerId: this.activeProvider()!.providerId,
        }),
      timezones: this.providerService.getTimezones(),
    };
  }

  protected getFetchPipeline<T>() {
    return (source: Observable<T>) =>
      source.pipe(
        take(1),
        catchError(() => {
          this.errorMessages.set([
            'Failed to fetch data for appointment booking.',
          ]);
          console.error('Failed to fetch data for appointment booking.');
          return EMPTY;
        }),
        finalize(() => {
          this.initialLoading.set(false);
        })
      );
  }

  protected applyCommonBookingData(data: {
    providerLanguages: string[];
    appointmentTypes: ProviderAppointmentType[];
    timezones: Timezone[];
    providerTimezone: ProviderTimezoneUpdateResponse | undefined;
  }): void {
    const { providerLanguages, appointmentTypes, timezones, providerTimezone } =
      data;

    this.providerLanguagesMap.clear();
    providerLanguages.forEach((languageCode: string) => {
      this.providerLanguagesMap.set(languageCode, {
        languageCode: languageCode,
        languageFlagUrl: Utils.getCountryFlagSvgPathByLanguage(languageCode),
        languageName: LANGUAGE_INFO[languageCode]?.name || languageCode,
      });
    });

    if (this.providerLanguagesMap.has('en')) {
      this.languageControl?.setValue('en', { emitEvent: false });
      this.languageControl.markAsPristine();
    }

    this.appointmentTypesMap.clear();
    appointmentTypes.forEach(type => {
      if (type.mode === ProviderAppointmentMode.Hybrid) {
        this.appointmentTypesMap.set(
          `${type.id}/${ProviderAppointmentMode.InPerson}`,
          { ...type, mode: ProviderAppointmentMode.InPerson }
        );
        this.appointmentTypesMap.set(
          `${type.id}/${ProviderAppointmentMode.Virtual}`,
          { ...type, mode: ProviderAppointmentMode.Virtual }
        );
      } else {
        this.appointmentTypesMap.set(`${type.id}/${type.mode}`, type);
      }
    });

    this.timezones = timezones;
    this.filteredTimezones = timezones;
    this.providerTimezone = providerTimezone?.timeZoneId;

    const requestedTimeZoneId = this.dialogData?.timeZoneId;

    if (requestedTimeZoneId) {
      this.timezoneIdControl?.setValue(requestedTimeZoneId, {
        emitEvent: false,
      });
      this.currentTimezone$.next(requestedTimeZoneId);
    } else {
      this.timezoneIdControl?.setValue(
        this.profileTimeZone.resolve(this.providerTimezone)
      );
    }

    this.noActiveTypes.set(this.appointmentTypesMap.size === 0);

    if (this.requestedStartUtc) {
      const options = Array.from(this.appointmentTypesMap.keys());

      if (options.length === 1) {
        this.appointmentTypeControl.setValue(options[0]);
      }

      this.dateControl.setValue(
        this.dateParser.dayKeyInZone(
          this.requestedStartUtc,
          this.timezoneIdControl?.value
        ),
        { emitEvent: false }
      );
    }

    this.appointmentForm.markAsPristine();
  }

  private applyRequestedStart(result: SlotsResponse, timezoneId: string): void {
    const requested = this.requestedStartUtc;

    if (!requested) return;

    if (!this.appointmentTypeControl.value) {
      this.requestedStartMessage.set(null);
      return;
    }

    const shownDayKey = this.dateControl.value;
    const current = this.timeslotControl.value;
    const invalidatedStart =
      current &&
      !(result.days[shownDayKey] ?? []).some(
        slot => Date.parse(slot.startUtc) === Date.parse(current)
      )
        ? current
        : null;

    if (invalidatedStart) {
      this.timeslotControl.setValue('', { emitEvent: false });
    }

    if (this.providerChoseTimeslot) {
      this.requestedStartMessage.set(
        invalidatedStart
          ? this.buildUnavailableMessage(invalidatedStart, timezoneId)
          : null
      );
      return;
    }

    const requestedDayKey = this.dateParser.dayKeyInZone(requested, timezoneId);
    const offered = (result.days[requestedDayKey] ?? []).find(
      slot => Date.parse(slot.startUtc) === Date.parse(requested)
    );

    if (
      offered &&
      shownDayKey === requestedDayKey &&
      !this.timeslotControl.value
    ) {
      this.timeslotControl.setValue(offered.startUtc, { emitEvent: false });
    }

    this.refreshRequestedStartMessage(result.days, timezoneId);
  }

  private refreshRequestedStartMessage(
    days: Record<string, SlotDay>,
    timezoneId: string = this.timezoneIdControl?.value
  ): void {
    const requested = this.requestedStartUtc;

    if (
      !requested ||
      !timezoneId ||
      this.providerChoseTimeslot ||
      !this.appointmentTypeControl.value
    ) {
      this.requestedStartMessage.set(null);
      return;
    }

    const requestedDayKey = this.dateParser.dayKeyInZone(requested, timezoneId);
    const requestedDaySlots = days[requestedDayKey] ?? [];
    const offered = requestedDaySlots.some(
      slot => Date.parse(slot.startUtc) === Date.parse(requested)
    );

    const canOfferAnotherTime =
      !offered &&
      requestedDaySlots.length > 0 &&
      this.dateControl.value === requestedDayKey &&
      this.errorMessages().length === 0;

    this.requestedStartMessage.set(
      canOfferAnotherTime
        ? this.buildUnavailableMessage(requested, timezoneId)
        : null
    );
  }

  private buildUnavailableMessage(
    requested: string,
    timezoneId: string
  ): string {
    const time = new Date(requested).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: timezoneId,
    });
    const typeName = this.displayAppointmentTypeFn(
      this.appointmentTypeControl.value
    );
    const duration = this.selectedAppointmentType?.durationMinutes;

    return `${time} isn't available for ${typeName}${
      duration ? ` (${duration} min)` : ''
    }. Choose another time.`;
  }

  protected setPatients(patients: ProviderPatient[]): void {
    this.patientsList = patients;
    this.filteredPatients = patients;
  }

  onDateSelected($event: Date) {
    this.selectedDate$.next($event);
  }

  onDateRangeSelected($event: { start: string; end: string }) {
    const startDate = new Date($event.start);
    const endDate = new Date($event.end);
    const middleTime = (startDate.getTime() + endDate.getTime()) / 2;
    const middleDate = new Date(middleTime);

    const firstDayOfFocusedMonth = new Date(
      middleDate.getFullYear(),
      middleDate.getMonth(),
      1
    );
    const firstDayString = `${firstDayOfFocusedMonth.getFullYear()}-${String(firstDayOfFocusedMonth.getMonth() + 1).padStart(2, '0')}-01`;
    if (this._isInitialRangeEmission) {
      // A requested start already put the date on the day the opener asked for; letting the
      // picker's first emission reset it to the first of the month would silently move it.
      if (!this.requestedStartUtc) {
        const today = new Date();
        const todayString = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
        if (!this.dateControl.value || this.dateControl.value !== todayString) {
          this.dateControl.setValue(firstDayString, { emitEvent: false });
        }
      }
      this._isInitialRangeEmission = false;
      this.dateControl.markAsPristine();
    } else {
      if (this.dateControl.value !== firstDayString) {
        this.dateControl.setValue(firstDayString, { emitEvent: false });
      }
    }

    this.selectedDateRange$.next($event);
  }

  getCountryFlagPath(languageCode: string): string {
    return Utils.getCountryFlagSvgPathByLanguage(languageCode);
  }

  submit(): void {
    this.appointmentForm.markAllAsTouched();
    triggerValidation(this.appointmentForm);
    if (!this.appointmentForm.valid) return;

    const value = this.appointmentForm.getRawValue();
    const [appointmentTypeId, appointmentMode] =
      value.appointmentType.split('/');

    this.createNewAppointment({
      appointmentTypeId: appointmentTypeId,
      startUtc: value.timeslot,
      patientId: value.patientId,
      appointmentLanguage: value.language,
      locationType: appointmentMode as
        | ProviderAppointmentMode.InPerson
        | ProviderAppointmentMode.Virtual,
      ...(this.isProviderDialog ? { note: value.note } : {}),
    });
  }

  getFormattedDate(): string {
    const timezoneAbbr =
      this.timezones.find(tz => tz.timeZoneId === this.timezoneIdControl?.value)
        ?.abbreviation || '';

    const date = new Date();

    const options: Intl.DateTimeFormatOptions = {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    };

    const formatted = date.toLocaleDateString('en', options);

    const parts = formatted.split(', ');
    if (parts.length >= 3) {
      const datePart = `${parts[0]}, ${parts[1]}`;
      const timePart = parts[2];

      return `${datePart} at ${timePart}${timezoneAbbr ? ` (${timezoneAbbr})` : ''}`;
    }

    return formatted;
  }

  abstract createNewAppointment(body: CreateProviderAppointmentDto): void;

  displayPatientFn(patientId: string): string {
    if (!patientId) return '';

    const patient = this.patientsList.find(
      listedPatient => listedPatient.id === patientId
    )!;

    if (!patient) {
      console.error('Could not find patient by id');
      return '';
    }

    return `${patient.givenName} ${patient.familyName}`;
  }

  displayAppointmentTypeFn(appointmentTypeId: string) {
    return this.appointmentTypeDisplayService.displayAppointmentTypeFn(
      appointmentTypeId,
      this.appointmentTypesMap
    );
  }

  private _filterTimezones(value: string): Timezone[] {
    const filterValue = value.toLowerCase();
    return this.timezones.filter(
      zone =>
        zone.fullName.toLowerCase().includes(filterValue) ||
        zone.timeZoneId.toLowerCase().includes(filterValue) ||
        zone.abbreviation.toLowerCase().includes(filterValue)
    );
  }

  private _filterPatients(value: string): ProviderPatient[] {
    const filterValue = value.toLowerCase();
    return this.patientsList.filter(patient =>
      `${patient.givenName} ${patient.familyName}`
        .toLowerCase()
        .includes(filterValue)
    );
  }

  discard(): void {
    this.dialogRef.close(null);
  }

  openAppointmentTypesManagement(event: Event): void {
    const url = this.appointmentTypesManagementUrl;
    if (!url) return;

    event.preventDefault();
    this.dialogRef.close(null);
    void this.router.navigateByUrl(url);
  }

  displayTimezoneFn = (zoneId: string) => {
    if (!zoneId) return '';
    const found = this.filteredTimezones.find(z => z.timeZoneId === zoneId);
    return found ? found.abbreviation : zoneId;
  };

  onTimezoneSelected(timezoneId: string): void {
    this.timezoneIdControl?.setValue(timezoneId, { emitEvent: false });
    this.timezoneIdControl?.markAsPristine();
    this.currentTimezone$.next(timezoneId);
  }

  protected createUnpaidAppointment(body: CreateProviderAppointmentDto) {
    this.appointmentsService
      .createProviderAppointment(this.activeProvider()!.providerId, body)
      .pipe(
        catchError(error => {
          this.errorMessages.set([
            'Failed to book the appointment. Please try again.',
          ]);
          console.error('Failed to book the appointment', error);
          return EMPTY;
        }),
        switchMap(appointment =>
          this.isPatientDialog
            ? this.appointmentConsent
                .signConsentForAppointment(appointment.id)
                .pipe(map(() => appointment))
            : of(appointment)
        ),
        finalize(() => {
          this.submitting.set(false);
        })
      )
      .subscribe(appointment => {
        this.dialogRef.close(appointment);
      });
  }
}
